import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MapPin, Building, Calendar } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import ImageLightbox from "./ImageLightbox";

interface Location {
  name: string;
  address: string;
  units: number;
  description?: string;
  amenities?: string[];
}

interface PropertyDetails {
  name: string;
  description: string;
  address: string;
  units: number;
  yearBuilt: number;
  rating: number;
  images: string[];
  amenities: string[];
  locations?: Location[];
}

interface PropertyDetailsPopupProps {
  property: PropertyDetails | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onContactClick?: () => void;
}

const PropertyDetailsPopup: React.FC<PropertyDetailsPopupProps> = ({
  property,
  open,
  onOpenChange,
  onContactClick,
}) => {
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  if (!property) return null;

  // If property has locations and no location is selected, default to first one
  const currentProperty = property.locations && property.locations.length > 0 
    ? (selectedLocation || property.locations[0])
    : property;

  const displayAddress = property.locations ? currentProperty.address : property.address;
  const displayUnits = property.locations ? currentProperty.units : property.units;
  const displayDescription = property.locations && currentProperty.description 
    ? currentProperty.description 
    : property.description;
  const displayAmenities = property.locations && currentProperty.amenities 
    ? [...property.amenities, ...currentProperty.amenities]
    : property.amenities;

  const handleImageClick = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const handleLightboxNext = () => {
    setLightboxIndex((prev) => (prev + 1) % property.images.length);
  };

  const handleLightboxPrevious = () => {
    setLightboxIndex((prev) => (prev - 1 + property.images.length) % property.images.length);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-3xl font-light text-foreground">
            {property.name}
          </DialogTitle>
          
          {/* Location Selector for Grey Studios */}
          {property.locations && property.locations.length > 0 && (
            <div className="mt-4">
              <Select 
                value={selectedLocation?.name || property.locations[0].name} 
                onValueChange={(value) => {
                  const location = property.locations?.find(loc => loc.name === value);
                  setSelectedLocation(location || null);
                }}
              >
                <SelectTrigger className="w-full max-w-md mx-auto bg-background border-2 border-muted z-50">
                  <SelectValue placeholder="Select Location" />
                </SelectTrigger>
                <SelectContent className="bg-background border-2 border-muted z-50">
                  {property.locations.map((location) => (
                    <SelectItem 
                      key={location.name} 
                      value={location.name}
                      className="hover:bg-muted/50"
                    >
                      {location.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Image Gallery Carousel */}
          <div className="relative">
            <Carousel className="w-full">
              <CarouselContent>
                {property.images.map((image, index) => (
                  <CarouselItem key={index}>
                    <div 
                      className="aspect-video overflow-hidden rounded-lg cursor-pointer"
                      onClick={() => handleImageClick(index)}
                    >
                      <img 
                        src={image} 
                        alt={`${property.name} - Image ${index + 1}`}
                        width="640"
                        height="360"
                        loading="lazy"
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="left-4" />
              <CarouselNext className="right-4" />
            </Carousel>
          </div>

          {/* Property Details */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-foreground">Property Details</h3>
              
              <div className="flex items-center space-x-2 text-muted-foreground">
                <MapPin size={16} />
                <span>{displayAddress}</span>
              </div>
              
              <div className="flex items-center space-x-2 text-muted-foreground">
                <Building size={16} />
                <span>{displayUnits} Units</span>
              </div>
              
              <div className="flex items-center space-x-2 text-muted-foreground">
                <Calendar size={16} />
                <span>Launched in {property.yearBuilt}</span>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="text-xl font-semibold text-foreground">Description</h3>
              <p className="text-muted-foreground leading-relaxed">
                {displayDescription}
              </p>
            </div>
          </div>

          {/* Amenities */}
          <div>
            <h3 className="text-xl font-semibold text-foreground mb-4">Amenities</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {displayAmenities.map((amenity, index) => (
                <div 
                  key={index}
                  className="px-3 py-2 bg-muted/50 rounded-lg text-sm text-muted-foreground"
                >
                  {amenity}
                </div>
              ))}
            </div>
          </div>

          {/* Contact CTA */}
          <div className="text-center pt-6 border-t">
            <Button 
              className="w-full md:w-auto"
              onClick={onContactClick}
            >
              Contact for More Information
            </Button>
          </div>
        </div>

        {/* Image Lightbox */}
        <ImageLightbox
          images={property.images}
          currentIndex={lightboxIndex}
          isOpen={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
          onNext={handleLightboxNext}
          onPrevious={handleLightboxPrevious}
        />
      </DialogContent>
    </Dialog>
  );
};

export default PropertyDetailsPopup;